// ShellContext — shared state owned by the Shell and consumed by views.
//
// Carries:
//   - the active project name (from .active-project; later: project picker)
//   - the chat-slide-over open/closed state
//   - the chat hook (so the slide-over can read messages and send)
//   - a way to push a "selected entity" into the right pin pane

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useChat, type ChatAPI } from "../lib/chat.js";
import type { Envelope } from "../lib/envelope.js";

interface SelectedEntity {
  // Free-form: a property-grid-shaped record from get_task / get_risk / etc.
  envelope: Envelope;
}

interface ShellContextValue {
  project: string;
  setProject: (name: string) => void;

  chatOpen: boolean;
  openChat: (prefill?: string) => void;
  closeChat: () => void;

  chat: ChatAPI;

  selected: SelectedEntity | null;
  setSelected: (entity: SelectedEntity | null) => void;

  // Prefill text staged for the next chat-open. Consumed by the slide-over.
  prefill: string;
  setPrefill: (s: string) => void;

  // Per-view content for the right pin pane. Views call `useRightPane(node)`
  // on mount; RightPinPane renders this above the default placeholder.
  viewRightPane: ReactNode | null;
  setViewRightPane: (node: ReactNode | null) => void;
}

const Ctx = createContext<ShellContextValue | null>(null);

// Default project name; matches the .active-project file at the repo root.
// A future project-picker (UI_SPEC §6.5) will populate this from
// list_projects() rather than a hard-coded constant.
const DEFAULT_PROJECT = "aio_pm_coe";

export function ShellProvider({ children }: { children: ReactNode }) {
  const chat = useChat();
  const [project, setProject] = useState<string>(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("project");
    return fromUrl || DEFAULT_PROJECT;
  });
  const [chatOpen, setChatOpen] = useState(false);
  const [prefill, setPrefill] = useState("");
  const [selected, setSelected] = useState<SelectedEntity | null>(null);
  const [viewRightPane, setViewRightPane] = useState<ReactNode | null>(null);

  const openChat = useCallback((text?: string) => {
    if (text !== undefined) setPrefill(text);
    setChatOpen(true);
  }, []);
  const closeChat = useCallback(() => setChatOpen(false), []);

  return (
    <Ctx.Provider value={{
      project, setProject,
      chatOpen, openChat, closeChat,
      chat,
      selected, setSelected,
      prefill, setPrefill,
      viewRightPane, setViewRightPane,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useShell(): ShellContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useShell called outside <ShellProvider>");
  return v;
}

/** Push per-view content into the right pin pane. Clears on unmount. */
export function useRightPane(node: ReactNode): void {
  const { setViewRightPane } = useShell();
  useEffect(() => {
    setViewRightPane(node);
    return () => setViewRightPane(null);
  }, [node, setViewRightPane]);
}
